import { useSelector } from "react-redux";
import { useEffect, useState, useRef, useCallback } from "react";
import type { RootState } from "./store/types";
import type { SSEOptions, SSEHookReturn, SSEMessage } from "./types";

export const useTypedSelector = useSelector.withTypes<RootState>();

const MAX_RETRIES = 5;
const BASE_BACKOFF_MS = 1000;

export const useSSE = (
  url: string | null,
  options: SSEOptions,
): SSEHookReturn => {
  const [isConnected, setIsConnected] = useState(false);
  const eventSourceRef = useRef<EventSource | null>(null);
  const retryCountRef = useRef(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Track whether the connection was intentionally closed by the consumer
  const intentionalCloseRef = useRef(false);
  // Use a ref to always access the latest options in event handlers,
  // avoiding stale closures since the effect only re-runs when url changes.
  const optionsRef = useRef(options);
  optionsRef.current = options;

  useEffect(() => {
    const timestamp = () => new Date().toISOString();

    console.log(`[${timestamp()}] useSSE effect running with URL:`, url);
    if (!url) {
      console.log(`[${timestamp()}] No URL provided, skipping SSE connection`);
      return;
    }

    intentionalCloseRef.current = false;
    retryCountRef.current = 0;

    const connect = (currentUrl: string) => {
      console.log(
        `[${timestamp()}] Creating new EventSource with URL:`,
        currentUrl,
      );
      const eventSource = new EventSource(currentUrl, {
        withCredentials: true, // Include auth cookies
      });

      eventSourceRef.current = eventSource;

      eventSource.onopen = () => {
        console.log(`[${timestamp()}] SSE connection opened`);
        retryCountRef.current = 0;
        setIsConnected(true);
        optionsRef.current.onOpen?.();
      };

      eventSource.onmessage = (event) => {
        try {
          const data: SSEMessage = JSON.parse(event.data);
          console.log(`[${timestamp()}] SSE message received:`, data);

          // Handle heartbeat messages (ignore them)
          if (data.type === "heartbeat") {
            console.log(`[${timestamp()}] Received heartbeat, ignoring`);
            return;
          }

          // Close connection immediately for completion messages to prevent auto-reconnect
          if (data.type === "completed" || data.type === "stream_ended") {
            console.log(
              `[${timestamp()}] Sync completed - closing SSE connection immediately`,
            );
            intentionalCloseRef.current = true;
            eventSource.close();
            setIsConnected(false);
          }

          optionsRef.current.onMessage(data);
        } catch (error) {
          console.error(`[${timestamp()}] Failed to parse SSE message:`, error);
        }
      };

      eventSource.onerror = () => {
        eventSource.close();
        setIsConnected(false);

        if (intentionalCloseRef.current) {
          console.log(`[${timestamp()}] SSE connection closed intentionally`);
          return;
        }

        if (retryCountRef.current < MAX_RETRIES) {
          const backoff = BASE_BACKOFF_MS * Math.pow(2, retryCountRef.current);
          retryCountRef.current += 1;
          console.log(
            `[${timestamp()}] SSE connection lost, retrying in ${backoff}ms (attempt ${retryCountRef.current}/${MAX_RETRIES})`,
          );
          retryTimerRef.current = setTimeout(
            () => connect(currentUrl),
            backoff,
          );
        } else {
          console.error(
            `[${timestamp()}] SSE connection failed after ${MAX_RETRIES} retries`,
          );
          optionsRef.current.onError?.(new Event("error"));
        }
      };
    };

    connect(url);

    return () => {
      console.log(`[${timestamp()}] SSE cleanup - closing connection`);
      intentionalCloseRef.current = true;
      if (retryTimerRef.current !== null) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
      if (
        eventSourceRef.current &&
        eventSourceRef.current.readyState !== EventSource.CLOSED
      ) {
        eventSourceRef.current.close();
      }
      setIsConnected(false);
    };
  }, [url]);

  const closeConnection = useCallback(() => {
    intentionalCloseRef.current = true;
    if (retryTimerRef.current !== null) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    eventSourceRef.current?.close();
    setIsConnected(false);
  }, []);

  return { isConnected, closeConnection };
};
